import { FC, PropsWithChildren, useMemo } from 'react';
import { Theme, ThemeOptions, ThemeProvider, createTheme, darken, lighten, useTheme } from '@mui/material';

/**
 * Builds a dark variant of the surrounding theme. Everything rendered inside (text, icons,
 * dividers, inputs, menus, list items) picks its colors from this palette, so no per-component
 * color overrides are needed.
 */
const selectedRow = (theme: Theme) => ({
  '&:focus-visible': { zIndex: 1 },
  '&&.Mui-selected': {
    backgroundColor: lighten(darken(theme.palette.primary.dark, 0.2), 0.2),
    '&, & *, & .MuiSvgIcon-root': { color: '#fff' },
    '&:hover': { backgroundColor: lighten(darken(theme.palette.primary.dark, 0.2), 0.28) },
  },
});

const createDarkSurfaceTheme = (outer: Theme) => {
  // Dark blue surface (primary.dark, slightly darkened).
  const surface = darken(outer.palette.primary.dark, 0.2);

  return createTheme(outer as ThemeOptions, {
    palette: {
      mode: 'dark',
      background: { paper: surface, default: surface },
      text: { primary: '#fff', secondary: 'rgba(255,255,255,0.8)' },
      divider: 'rgba(255,255,255,0.2)',
      // Secondary drives nav icons and the selected/hover item, so keep it visible on the surface.
      secondary: { main: '#fff', contrastText: '#fff' },
    },
    components: {
      // The app's list items paint the selected row from the item's color (secondary), which is
      // white here for icon contrast, so re-declare the selected row as a lighter box of the blue.
      MuiListItemButton: { styleOverrides: { root: ({ theme }: { theme: Theme }) => selectedRow(theme) } },
      // Keep the field label white while the select is focused or open (the default focused
      // color is the brand blue, which disappears on the dark surface).
      MuiInputLabel: {
        styleOverrides: {
          root: { color: 'rgba(255,255,255,0.8)', '&.Mui-focused': { color: '#fff' } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            // Same thin outline in every state (idle, hover, focused, open) so selecting never thickens it.
            '&& .MuiOutlinedInput-notchedOutline, &&:hover .MuiOutlinedInput-notchedOutline, &&.Mui-focused .MuiOutlinedInput-notchedOutline':
              { borderWidth: '1px !important', borderColor: 'rgba(255,255,255,0.6) !important' },
            // The app's base style adds a box-shadow ring on focused inputs and cancels it for
            // outlined ones; re-declare the cancel because this override replaces that rule.
            '&.Mui-focused': { boxShadow: 'none !important' },
            // Mouse clicks keep the thin outline, but keyboard focus gets a clear white ring.
            '&:has(:focus-visible)': { outline: '2px solid #fff', outlineOffset: 2 },
            '& .MuiSelect-select, & .MuiSelect-icon': { color: '#fff' },
          },
        },
      },
      // The brand-blue fill disappears on the dark surface, so use a white fill on a faint track.
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: 'rgba(255,255,255,0.25)' },
          bar: { backgroundColor: '#fff' },
        },
      },
      // Dropdown rows keep a white tint (not a blue shade) when selected or hovered.
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
            '&&.Mui-selected': {
              backgroundColor: 'rgba(255,255,255,0.28)',
              '&, & *, & .MuiSvgIcon-root': { color: '#fff' },
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
            },
          },
        },
      },
    },
  });
};

/**
 * Renders its children on a dark surface by nesting a dark theme. Portaled children (menus,
 * popovers) inherit it too because React context follows the component tree.
 */
export const DarkSurface: FC<PropsWithChildren> = ({ children }) => {
  const outer = useTheme();
  const darkTheme = useMemo(() => createDarkSurfaceTheme(outer), [outer]);

  return <ThemeProvider theme={darkTheme}>{children}</ThemeProvider>;
};
